<template>
  <div class="pp-root">
    <!-- Password gate -->
    <div
      v-if="!unlocked"
      class="pp-gatewrap"
    >
      <form
        class="pp-gate"
        @submit.prevent="submit"
      >
        <div class="pp-logo">Ez<b>Fix'd</b></div>
        <p class="pp-gatep">
          Partnership earnings — enter the access password to continue.
        </p>
        <label
          class="pp-gatelabel"
          for="pp-key"
        >Password</label>
        <input
          id="pp-key"
          v-model="passwordInput"
          type="password"
          autocomplete="current-password"
          autofocus
        >
        <button type="submit">View</button>
        <div
          v-if="wrongPassword"
          class="pp-err"
        >
          Incorrect password. Try again.
        </div>
      </form>
    </div>

    <!-- Calculator -->
    <div
      v-else
      class="pp-wrap"
    >
      <div class="pp-brand">
        <span class="pp-logo">Ez<b>Fix'd</b></span>
      </div>

      <header class="pp-header">
        <div class="pp-eyebrow">Partnership earnings</div>
        <h1 class="pp-h1">
          What Advantage earns <span class="pp-x">×</span> EzFix'd
        </h1>
        <p class="pp-lede">
          Move the levers to see your annual earnings under each structure,
          across volume and portfolio performance.
        </p>
      </header>

      <div class="pp-paths" role="tablist">
        <button
          class="pp-path"
          :data-active="path === 'A'"
          role="tab"
          @click="path = 'A'"
        >
          <div class="pp-pl"><span class="pp-dot" /> Advantage funds the loans</div>
          <div class="pp-pd">
            You put up capital, earning your funding return plus a 1.5% referral fee.
          </div>
        </button>
        <button
          class="pp-path"
          :data-active="path === 'B'"
          role="tab"
          @click="path = 'B'"
        >
          <div class="pp-pl"><span class="pp-dot" /> Advantage refers, shares performance</div>
          <div class="pp-pd">
            You earn a 1.5% referral plus 15% of how the book performs, up or down.
          </div>
        </button>
      </div>

      <div class="pp-grid">
        <div class="pp-card">
          <h2 class="pp-h2">The levers</h2>
          <div class="pp-ctrl">
            <div class="pp-top">
              <label>Monthly funded volume</label>
              <span class="pp-val">{{ volFmt(vol) }}</span>
            </div>
            <input
              v-model.number="vol"
              type="range"
              min="500000"
              max="8000000"
              step="250000"
            >
            <div class="pp-presets">
              <button
                :data-on="vol === 1000000"
                @click="vol = 1000000"
              >
                Low $1M
              </button>
              <button
                :data-on="vol === 3000000"
                @click="vol = 3000000"
              >
                Mid $3M
              </button>
              <button
                :data-on="vol === 5000000"
                @click="vol = 5000000"
              >
                High $5M
              </button>
            </div>
          </div>
          <div
            v-show="path === 'A'"
            class="pp-ctrl"
          >
            <div class="pp-top">
              <label>Your funding rate</label>
              <span class="pp-val">{{ pct(fund) }}</span>
            </div>
            <input
              v-model.number="fund"
              type="range"
              min="0.06"
              max="0.105"
              step="0.0025"
            >
            <div class="pp-baseline">
              Below target, clean credit lifts your return — your 15% of the
              upside. Above target, EzFix'd reprices to cover it instead of
              easing your return.
            </div>
          </div>
          <div class="pp-ctrl">
            <div class="pp-top">
              <label>Portfolio losses</label>
              <span class="pp-val">{{ pct(wo) }}</span>
            </div>
            <input
              v-model.number="wo"
              type="range"
              min="0.02"
              max="0.075"
              step="0.0025"
            >
            <div class="pp-baseline">
              Credit write-offs across the book. Target is 5.0%.
            </div>
          </div>
          <div
            v-show="path === 'B'"
            class="pp-ctrl"
          >
            <div class="pp-top">
              <label>EzFix'd funding improvement</label>
              <span class="pp-val">0.0% → {{ pct(fundingImprovement) }}</span>
            </div>
            <input
              v-model.number="fundingImprovement"
              type="range"
              min="0"
              max="0.03"
              step="0.0025"
            >
            <div class="pp-baseline">
              As EzFix'd locks in cheaper capital, the book's margin grows — and
              you share 15% of it.
            </div>
          </div>
        </div>

        <div>
          <div class="pp-hero">
            <div class="pp-who"><span class="pp-swatch" /> Advantage earns</div>
            <div class="pp-big">{{ money(calc.advTotal) }}</div>
            <div class="pp-rate">
              = <b>{{ pct(calc.advRate, 2) }}</b> of funded volume / yr
            </div>
            <div class="pp-lines">
              <div
                v-for="(l, i) in calc.advLines"
                :key="i"
                class="pp-ln"
              >
                <span>{{ l[0] }}</span><span>{{ money(l[1]) }}</span>
              </div>
            </div>
          </div>

          <div class="pp-card pp-split">
            <h2 class="pp-h2">How performance is shared</h2>
            <div class="pp-sub">
              You share 15% of every dollar the book beats target by.
              Delinquencies <i>above</i> target are covered by a rate increase —
              that downside isn't passed to you.
            </div>
            <div class="pp-sbar">
              <div
                class="pp-s pp-adv"
                style="flex: 15"
              >
                15%
              </div>
              <div
                class="pp-s pp-ezf"
                style="flex: 85"
              >
                85%
              </div>
            </div>
            <div class="pp-legend">
              <span>Advantage</span><span>EzFix'd</span>
            </div>
          </div>
        </div>
      </div>

      <!-- eslint-disable-next-line vue/no-v-html -->
      <div
        class="pp-explain"
        v-html="explainHtml"
      />

      <footer class="pp-footer">
        Illustrative figures for discussion purposes. All amounts annualized on
        funded principal. Performance share is trued up against actual portfolio
        results.
      </footer>
    </div>
  </div>
</template>

<script>
// Client-side gate only — not meant to be strong security, just enough to keep
// the page from casual view. Share the link as /partnership?key=<password>, or
// let the visitor type the password. Change the password here.
const ACCESS_PASSWORD = 'advantage2026'

// Internal EzFix'd economics are fixed here and never shown.
const ECON = {
  cof: 0.105,
  gy: 0.27,
  opex: 0.05,
  ref: 0.015,
  prot: 0.045,
  share: 0.15,
  ceil: 0.11,
}

// Target portfolio loss rate (shown in the UI as "target is 5.0%"). Losses ABOVE
// this are covered by repricing (interest hikes), not clawed back from Advantage,
// so the shared-performance math caps the loss input here. Below target still
// flows through as upside. Applies to both structures.
const LOSS_TARGET = 0.05

export default {
  name: 'Partnership',
  data() {
    return {
      unlocked: false,
      passwordInput: '',
      wrongPassword: false,
      path: 'A',
      vol: 3000000,
      fund: 0.105,
      wo: 0.05,
      fundingImprovement: 0,
    }
  },
  computed: {
    V() {
      return this.vol * 12
    },
    // Same math engine as the internal model.
    calc() {
      const { cof, gy, opex, ref, prot, share, ceil } = ECON
      const V = this.V
      const fund = this.fund
      const wo = this.wo
      // Delinquencies above target are absorbed by repricing, not taken from
      // Advantage — so cap the loss input at target. Below target still lifts
      // their number (asymmetric: upside kept, downside removed).
      const cappedLosses = Math.min(wo, LOSS_TARGET)
      if (this.path === 'A') {
        const delta = cappedLosses - LOSS_TARGET
        const rawEff = fund - share * delta
        const effFund = Math.max(0, Math.min(ceil, rawEff))
        const adj = fund - effFund
        const ceilingHit = rawEff > ceil + 1e-9
        const fundReturn = effFund * V
        const referral = ref * V
        const advTotal = fundReturn + referral
        const fundLabel =
          Math.abs(adj) > 0.00001 ? `Funding return (${this.pct(effFund)})` : 'Funding return'
        return {
          advTotal,
          V,
          effFund,
          adj,
          ceilingHit,
          advRate: advTotal / V,
          advLines: [
            [fundLabel, fundReturn],
            ['Referral fee (1.5%)', referral],
          ],
        }
      }
      // Cheaper capital lowers EzFix'd's cost of funds, which lifts the book's
      // margin — and Advantage shares 15% of that gain (kept internal; only the
      // improvement delta is ever surfaced in the UI).
      const costOfFunds = cof - this.fundingImprovement
      const netBefore = (gy - cappedLosses - opex - costOfFunds) * V
      const referral = ref * V
      const protAmt = prot * V
      const deviation = netBefore - referral - protAmt
      const advDev = share * deviation
      const advTotal = referral + advDev
      const devLabel = deviation >= 0 ? 'Upside share (15%)' : 'Shortfall share (15%)'
      return {
        advTotal,
        V,
        deviation,
        advRate: advTotal / V,
        advLines: [
          ['Referral fee (1.5%)', referral],
          [devLabel, advDev],
        ],
      }
    },
    explainHtml() {
      const r = this.calc
      const vol = this.volFmt(this.vol)
      const lossesHot = this.wo > LOSS_TARGET
      if (this.path === 'A') {
        let t
        if (lossesHot) {
          t = `Delinquencies are above the 5% target, but EzFix'd covers the excess by repricing rather than easing your return — so it holds at <b>${this.pct(
            r.effFund
          )}</b>.`
        } else if (r.adj < -0.00001) {
          t = `The book is running clean, so your funding return lifts to <b>${this.pct(
            r.effFund
          )}</b>${r.ceilingHit ? ' (capped)' : ''} — your 15% of the outperformance.`
        } else {
          t = `At target loss levels your funding return sits at <b>${this.pct(r.effFund)}</b>.`
        }
        return `At <b>${vol}/month</b> funded, Advantage earns <b>${this.moneyFull(
          r.advTotal
        )}/yr</b> — ${this.pct(
          r.advRate,
          2
        )} of funded volume — from your funding return plus the 1.5% referral fee. ${t} You share 15% of the upside, and delinquencies above target are covered by repricing rather than taken from you.`
      }
      const dev = r.deviation
      let t
      if (lossesHot) {
        t = `Delinquencies are above the 5% target, but EzFix'd absorbs the excess by repricing rather than cutting your share — so you still earn <b>${this.moneyFull(
          ECON.share * dev
        )}</b> on top of the referral, your 15% of the upside.`
      } else {
        t = `The book is beating target, so on top of the referral you earn <b>${this.moneyFull(
          ECON.share * dev
        )}</b> — your 15% of the upside.`
      }
      const fundingNote =
        this.fundingImprovement > 0
          ? ` The cheaper capital EzFix'd has secured flows into the shared margin too — funding gains lift your number, not just credit performance.`
          : ''
      return `At <b>${vol}/month</b> funded, Advantage earns <b>${this.moneyFull(
        r.advTotal
      )}/yr</b> — ${this.pct(
        r.advRate,
        2
      )} of funded volume. You refer, EzFix'd funds. ${t} You share 15% of the upside, and delinquency spikes are covered by repricing rather than taken from you.${fundingNote}`
    },
  },
  created() {
    const key = this.$route.query.key
    if (key && key === ACCESS_PASSWORD) this.unlocked = true
  },
  mounted() {
    // Load the display fonts on demand (this page only).
    if (typeof document !== 'undefined' && !document.getElementById('pp-fonts')) {
      const l = document.createElement('link')
      l.id = 'pp-fonts'
      l.rel = 'stylesheet'
      l.href =
        'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap'
      document.head.appendChild(l)
    }
  },
  methods: {
    submit() {
      if (this.passwordInput === ACCESS_PASSWORD) {
        this.unlocked = true
        this.wrongPassword = false
      } else {
        this.wrongPassword = true
      }
    },
    money(n) {
      const s = n < 0 ? '-' : ''
      const a = Math.abs(Math.round(n))
      if (a >= 1e6) return s + '$' + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.0+$/, '') + 'M'
      return s + '$' + a.toLocaleString('en-US')
    },
    moneyFull(n) {
      return (n < 0 ? '-' : '') + '$' + Math.abs(Math.round(n)).toLocaleString('en-US')
    },
    pct(x, d = 1) {
      return (x * 100).toFixed(d) + '%'
    },
    volFmt(v) {
      return '$' + (v / 1e6).toFixed(v % 1e6 === 0 ? 1 : 2).replace(/\.0$/, '.0') + 'M'
    },
  },
}
</script>

<style scoped>
.pp-root {
  --bg: #eceef2;
  --surface: #ffffff;
  --ink: #141c2b;
  --muted: #5c6675;
  --faint: #8a93a2;
  --line: #e2e6ec;
  --adv: #2563eb;
  --adv-soft: #e7effe;
  --adv-ink: #1447ab;
  --ezf: #0e9c6c;
  --radius: 14px;
  --sans: 'Inter', system-ui, -apple-system, sans-serif;
  --disp: 'Space Grotesk', system-ui, sans-serif;
  font-family: var(--sans);
  background: var(--bg);
  color: var(--ink);
  line-height: 1.55;
  font-size: 15px;
  padding: 28px 20px 64px;
  min-height: 100vh;
}
.pp-root * {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Password gate */
.pp-gatewrap {
  max-width: 360px;
  margin: 6vh auto 0;
}
.pp-gate {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px;
  box-shadow: 0 8px 30px rgba(20, 28, 43, 0.06);
}
.pp-gatep {
  color: var(--muted);
  font-size: 13.5px;
  margin: 2px 0 18px;
}
.pp-gatelabel {
  font-size: 12px;
  font-weight: 600;
  color: var(--faint);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.pp-gate input {
  width: 100%;
  margin: 8px 0 4px;
  padding: 11px 13px;
  border: 1.5px solid var(--line);
  border-radius: 9px;
  font-size: 15px;
  outline: none;
  font-family: var(--sans);
}
.pp-gate input:focus {
  border-color: var(--ink);
}
.pp-gate button {
  width: 100%;
  margin-top: 14px;
  padding: 11px;
  border: none;
  border-radius: 9px;
  background: var(--ink);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--sans);
}
.pp-err {
  color: #c0392b;
  font-size: 12.5px;
  margin-top: 10px;
}

/* Calculator */
.pp-wrap {
  max-width: 880px;
  margin: 0 auto;
}
.pp-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}
.pp-logo {
  font-family: var(--disp);
  font-weight: 700;
  font-size: 19px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pp-logo b {
  color: var(--ezf);
}
.pp-header {
  margin-bottom: 20px;
}
.pp-eyebrow {
  font-family: var(--disp);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--faint);
  font-weight: 500;
  margin-bottom: 8px;
}
.pp-h1 {
  font-family: var(--disp);
  font-size: clamp(23px, 4.2vw, 32px);
  font-weight: 600;
  line-height: 1.12;
  letter-spacing: -0.01em;
}
.pp-x {
  color: var(--faint);
  font-weight: 400;
  padding: 0 4px;
}
.pp-lede {
  color: var(--muted);
  font-size: 15px;
  margin-top: 8px;
  max-width: 56ch;
}

.pp-paths {
  display: flex;
  gap: 10px;
  margin: 22px 0 18px;
  flex-wrap: wrap;
}
.pp-path {
  flex: 1 1 240px;
  background: var(--surface);
  border: 1.5px solid var(--line);
  border-radius: var(--radius);
  padding: 14px 16px;
  cursor: pointer;
  transition: 0.15s;
  text-align: left;
  font-family: var(--sans);
}
.pp-path:hover {
  border-color: #c7cfdb;
}
.pp-pl {
  font-family: var(--disp);
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pp-pd {
  color: var(--muted);
  font-size: 13px;
  margin-top: 3px;
}
.pp-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--line);
  flex: none;
}
.pp-path[data-active='true'] {
  border-color: var(--ink);
}
.pp-path[data-active='true'] .pp-dot {
  background: var(--adv);
}

.pp-grid {
  display: grid;
  grid-template-columns: 290px 1fr;
  gap: 18px;
  align-items: start;
}
@media (max-width: 720px) {
  .pp-grid {
    grid-template-columns: 1fr;
  }
}
.pp-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 20px;
}
.pp-h2 {
  font-family: var(--disp);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--faint);
  font-weight: 600;
  margin-bottom: 16px;
}

.pp-ctrl {
  margin-bottom: 20px;
}
.pp-ctrl:last-child {
  margin-bottom: 0;
}
.pp-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.pp-ctrl label {
  font-size: 13px;
  font-weight: 500;
}
.pp-val {
  font-family: var(--disp);
  font-weight: 600;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}
.pp-presets {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}
.pp-presets button {
  flex: 1;
  font-family: var(--sans);
  font-size: 12px;
  font-weight: 500;
  color: var(--muted);
  background: #f4f6f9;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 6px 4px;
  cursor: pointer;
  transition: 0.12s;
}
.pp-presets button:hover {
  border-color: #c7cfdb;
}
.pp-presets button[data-on='true'] {
  background: var(--ink);
  color: #fff;
  border-color: var(--ink);
}
.pp-root input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 5px;
  border-radius: 5px;
  background: var(--line);
  outline: none;
  cursor: pointer;
  margin: 2px 0;
}
.pp-root input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--ink);
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(20, 28, 43, 0.18);
}
.pp-root input[type='range']::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--ink);
  cursor: pointer;
}
.pp-baseline {
  font-size: 11px;
  color: var(--faint);
  margin-top: 6px;
}

.pp-hero {
  background: var(--adv-soft);
  border: 1px solid #cadefb;
  border-radius: var(--radius);
  padding: 22px;
}
.pp-who {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--adv-ink);
}
.pp-swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  background: var(--adv);
}
.pp-big {
  font-family: var(--disp);
  font-weight: 700;
  font-size: clamp(32px, 6vw, 44px);
  letter-spacing: -0.02em;
  line-height: 1.03;
  margin: 12px 0 2px;
  color: var(--adv-ink);
  font-variant-numeric: tabular-nums;
}
.pp-rate {
  font-size: 13.5px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.pp-rate b {
  font-family: var(--disp);
  color: var(--ink);
  font-weight: 600;
}
.pp-lines {
  margin-top: 14px;
  border-top: 1px solid rgba(20, 28, 43, 0.08);
  padding-top: 12px;
}
.pp-ln {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: var(--muted);
  padding: 3px 0;
}
.pp-ln span:last-child {
  font-family: var(--disp);
  font-variant-numeric: tabular-nums;
  color: var(--ink);
  font-weight: 500;
}

.pp-split {
  margin-top: 16px;
}
.pp-split .pp-h2 {
  margin-bottom: 6px;
}
.pp-sub {
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 12px;
}
.pp-sbar {
  display: flex;
  width: 100%;
  height: 38px;
  border-radius: 9px;
  overflow: hidden;
}
.pp-s {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--disp);
  font-size: 12px;
  font-weight: 600;
  color: #fff;
}
.pp-adv {
  background: var(--adv);
}
.pp-ezf {
  background: var(--ezf);
}
.pp-legend {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: var(--muted);
}

.pp-explain {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 16px 20px;
  margin-top: 18px;
  font-size: 14px;
  color: var(--muted);
  line-height: 1.6;
}
.pp-explain /deep/ b {
  color: var(--ink);
  font-weight: 600;
}
.pp-footer {
  margin-top: 24px;
  font-size: 12px;
  color: var(--faint);
  line-height: 1.6;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/pages/partnership.vue